// Shared client state: game content, your character sheet and flash messages.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { api, ApiError } from './api.ts';
import { afterAction, afterError, afterRefresh } from './fx/feedback.ts';

export interface Flash {
  kind: 'good' | 'bad';
  title?: ReactNode;
  text: ReactNode;
}

export interface Game {
  content: any;
  me: any;
  user: { username: string; role: string } | null;
  away: any;
  whatsNew: any[];
  dismissWhatsNew: () => void;
  flash: Flash | null;
  setFlash: (f: Flash | null) => void;
  setMe: (me: any) => void;
  refresh: () => Promise<void>;
  /** POST an action; updates the stat bar and shows errors automatically. Returns the result or null. */
  act: (path: string, body?: unknown, opts?: { success?: (result: any) => Flash | null }) => Promise<any | null>;
  dismissAway: () => void;
  logout: () => Promise<void>;
  /** Items, jobs etc by id for quick lookup. */
  byId: { items: Map<string, any>; jobs: Map<string, any>; tiers: Map<string, any>; properties: Map<string, any>; collections: Map<string, any>; achievements: Map<string, any> };
}

const Ctx = createContext<Game | null>(null);

export function useGame(): Game {
  const g = useContext(Ctx);
  if (!g) throw new Error('useGame outside provider');
  return g;
}

function index(list: any[]): Map<string, any> {
  return new Map(list.map((x) => [x.id, x]));
}

export function GameProvider({ content, children, onLoggedOut }: { content: any; children: ReactNode; onLoggedOut: () => void }) {
  const [me, setMeState] = useState<any>(null);
  // The latest character sheet, so sounds and effects can compare before/after an action.
  const meRef = useRef<any>(null);
  const setMe = useCallback((m: any) => {
    meRef.current = m;
    setMeState(m);
  }, []);
  const [user, setUser] = useState<Game['user']>(null);
  const [away, setAway] = useState<any>(null);
  const [whatsNew, setWhatsNew] = useState<any[]>([]);
  const [flash, setFlash] = useState<Flash | null>(null);
  const [byId] = useState(() => ({
    items: index(content.items),
    jobs: index(content.jobs),
    tiers: index(content.tiers),
    properties: index(content.properties),
    collections: index(content.collections),
    achievements: index(content.achievements),
  }));

  const refresh = useCallback(async () => {
    try {
      const r = await api.get('/api/me');
      const prev = meRef.current;
      setMe(r.me);
      afterRefresh(prev, r.me, (window.location.hash.replace(/^#\/?/, '') || 'home').split('/')[0]);
      setUser(r.user);
      if (r.away) setAway(r.away);
      if (r.whatsNew?.length) setWhatsNew(r.whatsNew);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) onLoggedOut();
    }
  }, [onLoggedOut, setMe]);

  useEffect(() => {
    refresh();
    // Light polling keeps the stat bar honest (the server is the source of truth).
    const t = setInterval(refresh, 60_000);
    return () => clearInterval(t);
  }, [refresh]);

  const act = useCallback<Game['act']>(async (path, body, opts) => {
    try {
      const r = await api.post(path, body ?? {});
      const prev = meRef.current;
      if (r.me) setMe(r.me);
      try {
        afterAction(path, r.result ?? r, prev, r.me ?? prev);
      } catch { /* effects must never break the game */ }
      const f = opts?.success ? opts.success(r.result) : null;
      if (f) setFlash(f);
      return r.result ?? r;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        onLoggedOut();
        return null;
      }
      setFlash({ kind: 'bad', text: (e as Error).message });
      afterError();
      return null;
    }
  }, [onLoggedOut]);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } finally {
      onLoggedOut();
    }
  }, [onLoggedOut]);

  const dismissWhatsNew = useCallback(() => {
    setWhatsNew([]);
    api.post('/api/whats-new/seen').catch(() => {});
  }, []);

  const value: Game = { content, me, user, away, whatsNew, dismissWhatsNew, flash, setFlash, setMe, refresh, act, dismissAway: () => setAway(null), logout, byId };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// ---------------------------------------------------------------- formatting helpers
export const fmt = (n: number) => (n ?? 0).toLocaleString('en-US');
export const money = (n: number) => `$${fmt(n)}`;
export function ago(t: number, now = Date.now()): string {
  const s = Math.max(0, Math.floor((now - t) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  return `${Math.floor(s / 86400)} days ago`;
}
export function clock(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
export function duration(ms: number): string {
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'}`;
  const h = Math.floor(m / 60);
  if (h < 6 && m % 60) return `${h} hour${h === 1 ? '' : 's'} ${m % 60} min`;
  if (h < 48) return `${h} hour${h === 1 ? '' : 's'}`;
  return `${Math.floor(h / 24)} days`;
}
export const CLASS_INFO: Record<string, { name: string; perk: string }> = {
  fearless: { name: 'Fearless', perk: 'Stamina refills 20% faster.' },
  maniac: { name: 'Maniac', perk: 'Energy refills 20% faster.' },
  mogul: { name: 'Mogul', perk: '10% more cash from jobs and properties.' },
};
